import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
  Divider,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import { useFormContext, Controller } from "react-hook-form";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { AhuType } from "../../../models/unitType";
import { brandTranslation } from "../../../utils/brandUtils";

const UnitInformation = () => {
  const { control, watch, setValue } = useFormContext();
  const { t } = useTranslation();
  const theme = useTheme();
  const params = useParams();
  const baseUnitType = params?.unitType ? parseInt(params.unitType, 10) : null;
  const isLinePrime = baseUnitType === AhuType.LINE_PRIME;
  const { isClivet } = useSelector((state) => state.brand);
  console.log("UnitInformation rendered with baseUnitType:", baseUnitType);
  // Watch form values
  const airFlowCalc = watch("airFlowCalc") ?? true;
  const area = watch("area");
  const height = watch("height");
  const changeRate = watch("changeRate");
  const buildingType = watch("buildingType");
  const unitType = watch("Type");

  // Unit type options for compact/energy/aqua units
  const unitTypeOptions = (() => {
    if (baseUnitType === AhuType.COMPACT) {
      return [
        { value: AhuType.COMPACT, label: isClivet ? t("clivet.framelessSelect.productCards.compactAhu.compactHoriz") : t("unitType.compactPrimeHorizontal") },
        { value: AhuType.COMPACT_VERTICAL, label: isClivet ? t("clivet.framelessSelect.productCards.compactAhu.compactVert") : t("unitType.compactPrimeVertical"), disabled: isClivet },
      ];
    } else if (baseUnitType === 2) {
      const options = [
        { value: AhuType.ENERGY_PRIME_STANDARD, label: t(brandTranslation("unitType.energyPrimeStandard")) },
      ];
      if (!isClivet) {
        options.push({ value: AhuType.ENERGY_PRIME_GENIUS, label: "Energy:Prime Genius" });
      }
      return options;
    } else if (baseUnitType === 4) {
      return [
        { value: AhuType.AQUA_PRIME_STANDARD, label: "Aqua:Prime Standard", disabled: true },
        { value: AhuType.AQUA_PRIME_GENIUS, label: "Aqua:Prime Genius" },
      ];
    }
    return [];
  })();
  // Airflow ranges per unit type (min/max in m³/h)
  const flowRangeMap = {
    [AhuType.COMPACT]: { min: 600, max: 6300 },
    [AhuType.COMPACT_VERTICAL]: { min: 600, max: 6300 },
    [AhuType.ENERGY_PRIME_STANDARD]: { min: 800, max: 40000 },
    [AhuType.ENERGY_PRIME_GENIUS]: { min: 2000, max: 40000 },
    [AhuType.AQUA_PRIME_STANDARD]: { min: 0, max: 99999 }, // TODO: update with real values
    [AhuType.AQUA_PRIME_GENIUS]: { min: 0, max: 99999 }, // TODO: update with real values
    [AhuType.LINE_PRIME]: { min: 18000, max: 100000 },
  };
  // For energy/aqua the actual sub-type is chosen in the Type dropdown;
  // use that selected value to pick the correct range.
  const effectiveUnitType =
    (baseUnitType === AhuType.COMPACT || baseUnitType === 2 || baseUnitType === 4) && unitType != null
      ? unitType
      : baseUnitType;
  const flowRange = flowRangeMap[effectiveUnitType] ?? { min: 600, max: 40000 };

  // Building types options
  const building = [
    {
      value: "Residential - Basements (3-4)",
      label: t("buildingTypes.Residential - Basements (3-4)"),
    },
    {
      value: "Residential - Badrooms (5-6)",
      label: t("buildingTypes.Residential - Badrooms (5-6)"),
    },
    {
      value: "Residential - Bathrooms (6-7)",
      label: t("buildingTypes.Residential - Bathrooms (6-7)"),
    },
    {
      value: "Residential - Family Living Rooms (6-8)",
      label: t("buildingTypes.Residential - Family Living Rooms (6-8)"),
    },
    {
      value: "Residential - Kitchens (7-8)",
      label: t("buildingTypes.Residential - Kitchens (7-8)"),
    },
    {
      value: "Residential - Laudry (8-9)",
      label: t("buildingTypes.Residential - Laudry (8-9)"),
    },
    {
      value: "Light Commercial - Offices - Business Offices (6-8)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Business Offices (6-8)"
      ),
    },
    {
      value: "Light Commercial - Offices - Lunch Break Rooms (7-8)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Lunch Break Rooms (7-8)"
      ),
    },
    {
      value: "Light Commercial - Offices - Conference Rooms (8-12)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Conference Rooms (8-12)"
      ),
    },
    {
      value: "Light Commercial - Offices - Medical Procedure Offices (9-10)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Medical Procedure Offices (9-10)"
      ),
    },
    {
      value: "Light Commercial - Offices - Copy Rooms (10-12)",
      label: t("buildingTypes.Light Commercial - Offices - Copy Rooms (10-12)"),
    },
    {
      value: "Light Commercial - Offices - Main Computer Rooms (10-14)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Main Computer Rooms (10-14)"
      ),
    },
    {
      value: "Light Commercial - Offices - Smoking Area (13-15)",
      label: t(
        "buildingTypes.Light Commercial - Offices - Smoking Area (13-15)"
      ),
    },
    {
      value: "Light Commercial - Restaurants - Dining Area (8-10)",
      label: t(
        "buildingTypes.Light Commercial - Restaurants - Dining Area (8-10)"
      ),
    },
    {
      value: "Light Commercial - Restaurants - Food Staging (10-12)",
      label: t(
        "buildingTypes.Light Commercial - Restaurants - Food Staging (10-12)"
      ),
    },
    {
      value: "Light Commercial - Restaurants - Kitchens (14-18)",
      label: t(
        "buildingTypes.Light Commercial - Restaurants - Kitchens (14-18)"
      ),
    },
    {
      value: "Light Commercial - Restaurants - Bars (15-20)",
      label: t("buildingTypes.Light Commercial - Restaurants - Bars (15-20)"),
    },
    {
      value: "Light Commercial - Public Buildings - Hallways (6-8)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Hallways (6-8)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Retail Stores (6-10)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Retail Stores (6-10)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Foyers (8-10)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Foyers (8-10)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Churches (8-12)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Churches (8-12)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Restrooms (10-12)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Restrooms (10-12)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Auditoriums (12-14)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Auditoriums (12-14)"
      ),
    },
    {
      value: "Light Commercial - Public Buildings - Smoking Rooms (15-20)",
      label: t(
        "buildingTypes.Light Commercial - Public Buildings - Smoking Rooms (15-20)"
      ),
    },
    {
      value: "None",
      label: t("buildingTypes.None"),
    },
  ];

  // Validation rules
  const validationRules = {
    projName: {
      required: t("projectinformation.err") || "This field is required",
    },
    Type: {
      required:
        unitTypeOptions.length > 0
          ? t("projectinformation.err") || "This field is required"
          : false,
    },
    flowSupply: {
      required: t("projectinformation.err") || "This field is required",
      min: {
        value: flowRange.min,
        message: t("snackbar.invalidFlow", { min: flowRange.min, max: flowRange.max }),
      },
      max: {
        value: flowRange.max,
        message: t("snackbar.invalidFlow", { min: flowRange.min, max: flowRange.max }),
      },
    },
    flowReturn: {
      required: isLinePrime
        ? false
        : t("projectinformation.err") || "This field is required",
      min: isLinePrime
        ? undefined
        : {
          value: flowRange.min,
          message: t("snackbar.invalidFlow", { min: flowRange.min, max: flowRange.max }),
        },
      max: isLinePrime
        ? undefined
        : {
          value: flowRange.max,
          message: t("snackbar.invalidFlow", { min: flowRange.min, max: flowRange.max }),
        },
    },
    dropSupply: {
      required: t("projectinformation.err") || "This field is required",
      min: {
        value: 0,
        message: t("snackbar.invalidDrop", { min: 0, max: 1000 }),
      },
      max: {
        value: 1000,
        message: t("snackbar.invalidDrop", { min: 0, max: 1000 }),
      },
    },
    dropReturn: {
      required: isLinePrime
        ? false
        : t("projectinformation.err") || "This field is required",
      min: isLinePrime
        ? undefined
        : {
          value: 0,
          message: t("snackbar.invalidDrop", { min: 0, max: 1000 }),
        },
      max: isLinePrime
        ? undefined
        : {
          value: 1000,
          message: t("snackbar.invalidDrop", { min: 0, max: 1000 }),
        },
    },
    area: {
      required: !airFlowCalc
        ? t("projectinformation.err") || "This field is required"
        : false,
      min: {
        value: 0,
        message: t("projectinformation.err") || "Area must be greater than 0",
      },
    },
    height: {
      required: !airFlowCalc
        ? t("projectinformation.err") || "This field is required"
        : false,
      min: {
        value: 0,
        message: t("projectinformation.err") || "Height must be greater than 0",
      },
    },
    buildingType: {
      required: !airFlowCalc
        ? t("projectinformation.err") || "This field is required"
        : false,
    },
    changeRate: {
      required: !airFlowCalc
        ? t("projectinformation.err") || "This field is required"
        : false,
      min: {
        value: 0,
        message:
          t("projectinformation.err") || "Change rate must be greater than 0",
      },
    },
  };

  // Extract change rate from building type and update changeRate field
  useEffect(() => {
    if (!airFlowCalc && buildingType) {
      let airChangeValue;
      if (buildingType === "None") {
        airChangeValue = 0;
      } else {
        airChangeValue = buildingType.substring(
          buildingType.lastIndexOf("-") + 1,
          buildingType.lastIndexOf(")")
        );
      }
      setValue("changeRate", airChangeValue, { shouldValidate: true });
    }
  }, [buildingType, airFlowCalc, setValue]);

  // Calculate flow when calculator fields change
  useEffect(() => {
    if (!airFlowCalc && area && height && changeRate) {
      const calculatedFlow = Math.round(area * height * changeRate);
      setValue("flowSupply", calculatedFlow, { shouldValidate: true });
      setValue("flowReturn", calculatedFlow, { shouldValidate: true });
    }
  }, [airFlowCalc, area, height, changeRate, setValue]);

  const preventInvalidInput = (e) => {
    if (["-", "+", "e", "E"].includes(e.key)) {
      e.preventDefault();
    }
  }

  const preventCopyPasteNegativeValue = (e, field) => {
    const v = Number(e.target.value);
    if (v === "" || (Number(v) >= 0 && Number(v) <= 1000)) {
      field.onChange(v);
    }

  }


  return (
    <Container component="main" sx={{ mb: 4, "min-width": "100%" }}>
      <Paper
        variant="outlined"
        sx={{
          marginTop: "0px",
          p: { xs: 2, md: 3 },
          align: "center",
          width: "100%",
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap" }}>
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Typography
              sx={{ textAlign: "center", fontSize: "38px", mb: "20px" }}
            >
              {t("mainform.info")}
            </Typography>
          </Box>

          <Controller
            name="projName"
            control={control}
            rules={validationRules.projName}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label={t("projectinformation.name")}
                type="text"
                InputLabelProps={{ shrink: true }}
                required
                error={!!error}
                helperText={error?.message}
              />
            )}
          />

          {unitTypeOptions.length > 0 && (
            <Controller
              name="Type"
              control={control}
              rules={validationRules.Type}
              render={({ field, fieldState: { error } }) => (
                <FormControl fullWidth required error={!!error} sx={{ mt: 2, mb: 1 }}>
                  <InputLabel id="unit-type-label" sx={{ color: "#373437" }}>
                    {t("unitType.label2")}
                  </InputLabel>
                  <Select
                    {...field}
                    labelId="unit-type-label"
                    label={t("unitType.label2")}
                    disablePortal
                    MenuProps={{ disableScrollLock: true }}
                    fullWidth
                  >
                    {unitTypeOptions.map((option) => (
                      <MenuItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled === true}
                      >
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {error && <FormHelperText>{error.message}</FormHelperText>}
                </FormControl>
              )}
            />
          )}

          <Box
            sx={{
              display: "flex",
              width: "100%",
              justifyContent: "center",
              alignItems: "center",
              margin: "20px 0",
            }}
          >
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: "14px", sm: "16px" },
                cursor: "pointer",
              }}
            >
              {t("projectinformation.item1")}
            </Typography>
            <Controller
              name="airFlowCalc"
              control={control}
              render={({ field: { value, onChange } }) => (
                <FormControlLabel
                  control={
                    <Switch
                      checked={value ?? true}
                      onChange={(e) => onChange(e.target.checked)}
                      sx={{ ml: 2, color: theme.palette.primary.main }}
                    />
                  }
                  sx={{ mb: 0 }}
                />
              )}
            />
            <Typography
              variant="body1"
              textAlign="center"
              sx={{
                color: theme.palette.primary.main,
                fontSize: { xs: "14px", sm: "16px" },
                cursor: "pointer",
              }}
            >
              {t("projectinformation.item2")}
            </Typography>
          </Box>
        </Box>

        {/* Group A: Air flow fields - Always visible */}
        <Box
          component="form"
          sx={{ "& .MuiTextField-root": { width: "41%" } }}
          noValidate
          autoComplete="off"
        >
           <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: { sm: 2 },
              }}
            >
            <Controller
              name="flowSupply"
              control={control}
              rules={validationRules.flowSupply}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  value={field.value ?? ""}
                  fullWidth
                  id="flowSupply"
                  label={t("projectinformation.flowsupplay")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  helperText={error?.message}
                  error={!!error}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">m³/h</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
            <Controller
              name="dropSupply"
              control={control}
              rules={validationRules.dropSupply}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  fullWidth
                  id="dropSupply"
                  label={t("projectinformation.dropsupplay")}
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  helperText={error?.message}
                  error={!!error}
                  sx={{ float: "right" }}
                  inputProps={{ min: 0, max: 1000 }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="start">Pa</InputAdornment>
                    ),
                    onWheel: (e) => e.target.blur(),
                  }}
                />
              )}
            />
          </Box>
          {!isLinePrime && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: { sm: 2 },
              }}
            >
              <Controller
                name="flowReturn"
                control={control}
                rules={validationRules.flowReturn}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    value={field.value ?? ""}
                    fullWidth
                    id="flowReturn"
                    label={t("projectinformation.flowreturn")}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    helperText={error?.message}
                    error={!!error}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">m³/h</InputAdornment>
                      ),
                      onWheel: (e) => e.target.blur(),
                    }}
                  />
                )}
              />
              <Controller
                name="dropReturn"
                control={control}
                rules={validationRules.dropReturn}
                render={({ field, fieldState: { error } }) => (
                  <TextField
                    {...field}
                    fullWidth
                    id="dropReturn"
                    label={t("projectinformation.dropreturn")}
                    type="number"
                    InputLabelProps={{ shrink: true }}
                    helperText={error?.message}
                    error={!!error}
                    inputProps={{ min: 0, max: 1000 }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="start">Pa</InputAdornment>
                      ),
                      onWheel: (e) => e.target.blur(),
                    }}
                  />
                )}
              />
            </Box>
          )}
        </Box>

        {/* Group B: Area/Height/Building Type/Change Rate - Only visible when calculator mode is OFF */}
        {!airFlowCalc && (
          <>
            <Box sx={{ m: 1 }}>
              <Divider sx={{ mb: 3, mt: 3 }} />
            </Box>
            <Box>
              <Box
                component="form"
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  "& .MuiTextField-root": { m: 1, width: "41%" },
                  marginBottom: "20px",
                }}
                noValidate
                autoComplete="off"
              >
                <Controller
                  name="area"
                  control={control}
                  rules={validationRules.area}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        field.onChange(value);
                      }}
                      margin="normal"
                      fullWidth
                      id="area"
                      label={t("projectinformation.area")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">m²</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
                <Controller
                  name="height"
                  control={control}
                  rules={validationRules.height}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        field.onChange(value);
                      }}
                      margin="normal"
                      fullWidth
                      id="height"
                      label={t("projectinformation.height")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        step: 0.1,
                      }}
                      helperText={error?.message}
                      error={!!error}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="start">m</InputAdornment>
                        ),
                        onWheel: (e) => e.target.blur(),
                      }}
                    />
                  )}
                />
              </Box>
              <Box
                component="form"
                sx={{
                  "& .MuiTextField-root": { m: 1, width: "41%" },
                  display: "flex",
                  justifyContent: "space-between",
                }}
                noValidate
                autoComplete="off"
              >
                <FormControl sx={{ ml: 1, width: "41%" }}>
                  <InputLabel
                    id="select-label"
                    sx={{ mb: 1, color: "#373437" }}
                  >
                    {t("projectinformation.buildingType")}
                  </InputLabel>
                  <Controller
                    name="buildingType"
                    control={control}
                    rules={validationRules.buildingType}
                    render={({ field, fieldState: { error } }) => (
                      <Select
                        {...field}
                        disablePortal
                        sx={{
                          mb: 1,
                          width: "100%",
                        }}
                        MenuProps={{ disableScrollLock: true }}
                        labelId="select-label"
                        label={t("projectinformation.buildingType")}
                        error={!!error}
                      >
                        {building.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                </FormControl>

                <Controller
                  name="changeRate"
                  control={control}
                  rules={validationRules.changeRate}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      value={field.value ?? ""}
                      onChange={(e) => {
                        const value = Number(e.target.value);
                        field.onChange(value);
                      }}
                      margin="normal"
                      fullWidth
                      id="changeRate"
                      label={t("projectinformation.changerate")}
                      type="number"
                      InputLabelProps={{ shrink: true }}
                      helperText={error?.message}
                      InputProps={{
                        onWheel: (e) => e.target.blur(),
                      }}
                      error={!!error}
                    />
                  )}
                />
              </Box>
            </Box>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default UnitInformation;
